import {
  isKonekoMemberExcludedForMonth,
  resolveKonekoAPlanningUsernameKey,
} from "@/config/konekoAConfig";
import {
  PAYROLL_EXCLUDED_FROM_MONTH,
  PAYROLL_ROSTER_NAME_ALIASES,
} from "@/config/payrollEmployeeRoster";
import {isRealTeamMemberRow} from "@/Utilites/teamRosterApi";
import {memberIncludedInPayrollMonth} from "@/utils/memberEmploymentDates";
import {resolveTeamRowUserId} from "@/utils/timesheetRosterLookup";

function normalizeKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

/** Map a payroll EMP row to a planning / roster exclusion key. */
export function resolvePayrollRosterPlanningKey(rosterRow) {
  if (!rosterRow) return "";
  const candidates = [
    rosterRow.name,
    rosterRow.nameInKoneko,
    rosterRow.fullName,
    rosterRow.empOutId,
  ];
  for (const raw of candidates) {
    const planning = resolveKonekoAPlanningUsernameKey(raw);
    if (planning) return planning;
  }
  const alias = PAYROLL_ROSTER_NAME_ALIASES[normalizeKey(rosterRow.name)];
  if (alias) {
    return resolveKonekoAPlanningUsernameKey(alias) || normalizeKey(alias);
  }
  return normalizeKey(rosterRow.name);
}

function excludedFromMonthForKey(key) {
  if (!key) return "";
  const direct = PAYROLL_EXCLUDED_FROM_MONTH[key];
  if (direct) return String(direct);
  const norm = normalizeKey(key);
  return PAYROLL_EXCLUDED_FROM_MONTH[norm] || "";
}

/** Static + Koneko access exclusions when backend employment fields lag. */
export function isPayrollRosterStaticallyExcluded(
  rosterRow,
  monthKey,
  rosterMetaByUsername = null
) {
  if (!rosterRow || !monthKey) return false;
  const mk = String(monthKey).trim();
  const byName = excludedFromMonthForKey(rosterRow.name);
  if (byName && mk >= byName) return true;

  const planningKey = resolvePayrollRosterPlanningKey(rosterRow);
  const byPlanning = excludedFromMonthForKey(planningKey);
  if (byPlanning && mk >= byPlanning) return true;

  if (
    planningKey &&
    isKonekoMemberExcludedForMonth(
      mk,
      planningKey,
      rosterMetaByUsername,
      rosterRow.nameInKoneko || rosterRow.fullName || rosterRow.name
    )
  ) {
    return true;
  }
  return false;
}

function teamRowForUserId(teamRows, userId) {
  if (userId == null) return null;
  return (
    (teamRows || []).find((row) => resolveTeamRowUserId(row) === userId) || null
  );
}

/**
 * Whether a merged EMP report row belongs in payroll for the selected month.
 * Requires a real nekoapi Member row — timesheet-only backfill is not enough.
 *
 * @param {object} reportRow
 * @param {object[]} apiTeamRows team-members API rows (not timesheet-enriched)
 * @param {string} monthKey YYYY-MM
 * @param {Record<string, object>} [rosterMetaByUsername]
 */
export function payrollReportRowIncludedForMonth(
  reportRow,
  apiTeamRows,
  monthKey,
  rosterMetaByUsername = null
) {
  if (!reportRow || !monthKey) return false;

  if (
    isPayrollRosterStaticallyExcluded(
      reportRow,
      monthKey,
      rosterMetaByUsername
    )
  ) {
    return false;
  }

  if (reportRow.userId == null) return false;

  const teamRow = teamRowForUserId(apiTeamRows, reportRow.userId);
  if (!isRealTeamMemberRow(teamRow)) return false;

  return memberIncludedInPayrollMonth(teamRow, monthKey);
}
